import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import { addMinutes, endOfMonth, format, getDay, isBefore, parse, previousSunday, setHours, setMinutes, startOfMonth, startOfWeek } from 'date-fns';
import { setDay } from 'date-fns/esm';
import it from 'date-fns/locale/it';
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Event, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button, Card, Col, Container, Form, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Devices } from '../../api/Devices';
import { Me } from '../../api/Me';
import { Patients } from '../../api/Patients';
import { Protocols } from '../../api/Protocols';
import AppLoader from '../../components/AppLoader';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Device } from '../../models/Device';
import { Protocol } from '../../models/Protocol';
import { ScheduledTherapies } from '../../models/ScheduledTherapies';
import { User, UserRole } from '../../models/User';
import './SchedulerPage.scss';


const locales = { 'it': it }
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
})




export default function SchedulerPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [myEvents, setEvents] = useState<any[]>([]);
    const [date, setDate] = useState<Date>(new Date())
    const [protocols, setProtocols] = useState<Protocol[] | null>();

    const [users, setPatients] = useState<User[] | null>();

    const [devicesList, setDeviceList] = useState<Device[]>();

    const [selectedPatient, setSelectedPatient] = useState<number>();

    const [showSelectPatientModal, setSelectPatientModal] = useState<boolean>(false);

    const [showSessionDetailModal, setShowSessionDetailModal] = useState<boolean>(false);
    const [sessionDetail, setSessionDetail] = useState<Event>()

    const [filterDevice, setFilterDevice] = useState<number>(0);
    const [filterPatient, setFilterPatient] = useState<number>(0);

    const { loggedUser } = useAuth();
    const { showLoader, hideLoader } = useFullScreenLoader();
    const { showToast } = useToasts();

    useEffect(() => {

        if (loggedUser) {
            initProtocols();
            if (loggedUser.role !== UserRole.PATIENT && loggedUser.role !== UserRole.CAREGIVER ) {
                initDeviceList();
                initPatients();
            }

        }
        // eslint-disable-next-line
    }, [loggedUser]);

    const initProtocols = () => {
        showLoader!();
        Protocols.getTenantProtocols()
            .then(
                ({ data: protocols }) => {
                    protocols.map((p) => { p.checked = false; return p });
                    setProtocols(protocols)
                    initScheduledTherapies(protocols);
                    hideLoader!()
                },
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    hideLoader!();
                    showToast!('protocols-error', message, `Protocols Error ${status ? `(${status})` : ''}`, 'danger');
                }
            );
    }

    const initDeviceList = () => {

        Devices.getDevicesList()
            .then(
                ({ data: devices }) => {

                    setDeviceList(devices)
                    hideLoader!()
                },
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    hideLoader!();
                    showToast!('devices-error', message, `Devices Error ${status ? `(${status})` : ''}`, 'danger');
                }
            );
    }

    const initPatients = () => {

        Patients.getPatients()
            .then(
                ({ data: patients }) => setPatients(patients.results),
                (err) => {
                    const message = err.response?.data?.message || err.message;
                    const status = err.response?.status;
                    showToast!('login-error', message, `Login Error ${status ? `(${status})` : ''}`, 'danger');
                }
            );

    }

    function initScheduledTherapies(protocols: Protocol[], start?: Date, end?: Date) {
        showLoader!();
        Me.getMeCalendar(format(start ? start : startOfMonth(date), 'yyyy-MM-dd'), format(end ? end : endOfMonth(date), 'yyyy-MM-dd'), { params: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } }).then(
            ({ data: scheduledTherapies }: AxiosResponse<ScheduledTherapies>) => {


                let scheduledEvent = scheduledTherapies.schedule.map((schedule) => {

                    let date = parse(schedule.date, 'yyyy-MM-dd', new Date());

                    let mappedSchedule = schedule.therapies.map((therapy) => {

                        let [hours, minutes] = therapy.time.split(':');
                        let calendarDate = setHours(setMinutes(date, Number(minutes)), Number(hours));

                        let title = therapy.protocolName! + '-' + therapy.sessionName;

                        return {
                            allDay: false,
                            title: title,
                            start: calendarDate,
                            end: addMinutes(calendarDate, therapy.duration || 30),
                            resource: { ...therapy },
                        }
                    })

                    return mappedSchedule;

                })

                let updatedEvents = [];
                updatedEvents.push(...scheduledEvent.flat());
                setEvents(updatedEvents)

                hideLoader!()
            },
            (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                hideLoader!();
                showToast!('patientSchedule-error', message, `Patient Schedule Error ${status ? `(${status})` : ''}`, 'danger');
            }
        );
    }

    const handleSelectPatientChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const value = evt.target.value;
        setSelectedPatient(Number(value));
    }

    const handleDeviceFilterChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const value = evt.target.value;
        setFilterDevice(Number(value))
    }
    const handlePatientFilterChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
        const value = evt.target.value;
        setFilterPatient(Number(value))
    }

    const onNavigate = useCallback((newDate: Date) => {
        setDate(newDate)

    }, [setDate])

    const dayPropGetter = useCallback(
        (date: Date) => ({
            ...(isBefore(date, new Date()) && {
                className: 'rbc-off-range-bg'
            }),
        }),
        []
    )

    const handleSelectEvent = useCallback(
        (event: any) => {
            openSessionDetailModal(event);
        },
        []
    )

    const handleGoTo = (evt: React.ChangeEvent<HTMLInputElement>) => {
        let destination = setDay(parse(evt.target.value, 'yyyy-MM', new Date()), 15)
        setDate(destination)
        handleRangeChange({ start: destination, end: destination })

    }

    const handleRangeChange = (evt: any) => {
        console.log(evt)
        if (Array.isArray(evt)) {
            return
        }

        let monthVisualized = previousSunday(evt.end)

        initScheduledTherapies(protocols!, startOfMonth(monthVisualized), endOfMonth(monthVisualized));

    };


    const eventPropGetter =
        (event: Event, start: any, end: any, isSelected: boolean) => {

            return {
                ...(event.resource.isCompleted && {
                    style: {
                        backgroundColor: '#71C562',
                    },
                }),

            }
        }




    const openSelectPatientModal = () => setSelectPatientModal(true);
    const handleSelectPatientModalClose = () => {
        setSelectPatientModal(false);
        setSelectedPatient(undefined);
    }

    const openSessionDetailModal = (evt: Event) => {
        setSessionDetail(evt);
        setShowSessionDetailModal(true)
    };

    const handleSessionDetailModalClose = () => {
        setShowSessionDetailModal(false);
        setSessionDetail(undefined)
    }

    const goToSchedulerPage = () => {
        setSelectPatientModal(false);
        navigate(`/patients/${selectedPatient}/calendar`)
    }

    const renderScheduler = () => {
        const filteredEvents = myEvents!
            .filter(evt => filterDevice === 0 || evt.resource.deviceId === filterDevice)
            .filter(evt => filterPatient === 0 || evt.resource.patientId === filterPatient);

        return (
            <>
                <Row className="gx-2 mb-3">
                    <Calendar
                        dayLayoutAlgorithm={'no-overlap'}
                        localizer={localizer}
                        events={filteredEvents}
                        startAccessor="start"
                        culture='it'
                        endAccessor="end"
                        eventPropGetter={eventPropGetter}
                        dayPropGetter={dayPropGetter}
                        onRangeChange={handleRangeChange}
                        onSelectEvent={handleSelectEvent}
                        views={['month', 'week', 'day']}
                        onNavigate={onNavigate}
                        selectable={false}
                        date={date}
                        className='calendar-readonly'
                    />
                </Row>
            </>
        )
    }


    const renderAsyncContent = () => {
        return myEvents
            ? renderScheduler()
            : (
                <div className="d-flex justify-content-center py-5">
                    <AppLoader size={150} />
                </div>
            )
    }

    return (
        <Container fluid>
            <Card>
                <Row className="d-flex align-items-center justify-content-between border-bottom py-1 px-3">
                    <Col md={3}>
                        <Card.Title className="mb-0 py-3">{loggedUser!.role === UserRole.DOCTOR ? 'Calendario Dottore' : 'Calendario Paziente'}{/*t('sessionEdit.title')*/}</Card.Title>
                    </Col>
                    <Col md={6} className='d-flex justify-content-between align-items-center'>
                        <div className='d-flex align-items-center justify-content-center w-100'>
                            <h6 className='mb-0 me-2'>Vai a</h6>
                            <div className="w-50 ">
                                <input className="datepicker" type='month' value={format(date, 'yyyy-MM')} onChange={handleGoTo}></input>
                            </div>
                        </div>
                        {(loggedUser!?.role !== UserRole.PATIENT && loggedUser!?.role !== UserRole.CAREGIVER) && <>
                            <div className='d-flex align-items-center justify-content-center w-100'>
                                <h6 className='mb-0 me-2'>Filtro Device</h6>
                                <div className="w-50 ">
                                    <Form.Select size="sm" onChange={handleDeviceFilterChange} >
                                        <option key="default" value={0}>Tutti i device</option>
                                        {devicesList && devicesList.map(dev => (
                                            <option key={dev.id} value={dev.id}>{dev.label}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>
                            <div className='d-flex align-items-center justify-content-center w-100'>
                                <h6 className='mb-0 me-2'>Filtro Paziente</h6>
                                <div className="w-50 ">
                                    <Form.Select size="sm" onChange={handlePatientFilterChange} >
                                        <option key="default" value={0}>Tutti i pazienti</option>
                                        {users && users.map(pat => (
                                            <option key={pat.id} value={pat.id}>{`${pat.firstname} ${pat.lastname}`}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>
                        </>
                        }
                    </Col>

                    <Col md={3} className='d-flex justify-content-end'>

                        {loggedUser!?.role !== UserRole.PATIENT && users?.length! > 0 &&
                            <Button className="mb-0" variant="success" size="sm" onClick={openSelectPatientModal}>
                                <FontAwesomeIcon icon={solid('plus')} />
                                <span className="ms-2">Schedula protocollo per paziente</span>
                            </Button>
                        }

                    </Col>

                </Row>
                <Row>
                    <Col>
                        <Card.Body>
                            {renderAsyncContent()}
                        </Card.Body>
                    </Col>
                </Row>
            </Card>

            <Modal show={showSelectPatientModal} onHide={handleSelectPatientModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Selezione paziente</Modal.Title>
                </Modal.Header>

                <Form >
                    <Modal.Body>
                        <Form.Group controlId="selected-protocol" className="mb-3">
                            <Form.Label>{t('common.patient')}</Form.Label>
                            <Form.Select size="sm" onChange={handleSelectPatientChange}>
                                <option key="default" value="Seleziona paziente" selected disabled>Seleziona paziente</option>
                                {users && users.map(pat => (
                                    <option key={pat.id} value={pat.id}>{`${pat.firstname} ${pat.lastname}`}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="light" className="border" size="sm" onClick={handleSelectPatientModalClose}>{t('common.cancel')}</Button>
                        <Button variant="success" size="sm" onClick={goToSchedulerPage}>{t('common.confirm')} </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showSessionDetailModal} onHide={handleSessionDetailModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>  {(sessionDetail?.title as String)?.split('-')[0]}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table className="">
                        <thead className='table-light'>
                            <tr>
                                <th scope="col" colSpan={2}>{(sessionDetail?.title as String)?.split('-')[1]}</th>
                            </tr>
                        </thead>
                        <tbody className="">
                            <tr>
                                <th scope="row">Orario</th>

                                <td>{sessionDetail?.resource.time}</td>
                            </tr>
                            <tr>
                                <th scope="row">Durata</th>
                                <td>{sessionDetail?.resource.duration} min</td>
                            </tr>
                            <tr>
                                <th scope="row">Device</th>
                                <td>{devicesList?.find(dev => dev.id === sessionDetail?.resource.deviceId)?.label}</td>
                            </tr>
                            <tr>
                                <th scope="row">Paziente</th>
                                <td>{sessionDetail?.resource.patientName}</td>
                            </tr>
                            <tr>
                                <th scope="row">Completato</th>
                                <td>{sessionDetail?.resource.isCompleted ? 'Si' : 'No'}</td>
                            </tr>
                        </tbody>
                    </Table>
                    {sessionDetail?.resource?.isCompleted ?
                        <Button className="float-end" onClick={() => navigate(`/therapy/${sessionDetail!.resource.therapyId}`)}> Show Results</Button>
                        : <></>
                    }
                </Modal.Body>
            </Modal>

        </Container >
    )
}